import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { RefDataService } from '@services/ref-data.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ConfigService } from '@services/config.service';
import { PackLite } from '@shared/models/generic/pack-lite.model';
import { DataRef, Domain, Language, Market, PackType, Status, TargetDomain } from '@shared/models/dataref.model';
import { PackTargetDomainsComponent } from './pack-target-domains/pack-target-domains.component';
import { EditPackNamePopinComponent } from './pack-edit-name-popin/edit-pack-name-popin.component';
import { MatDialog } from '@angular/material/dialog';
import { hasTargetDomainForBinaries } from '@packs/utils/pack.utils';

@Component({
  selector: 'app-pack-tab-component',
  templateUrl: './pack-tab.component.html',
  styleUrls: ['./pack-tab.scss'],
  standalone: false,
})
export class PackTabComponent implements OnInit, OnChanges {
  @Input() isEdit = false;
  @Input() formPack: UntypedFormGroup = new UntypedFormGroup({});
  @Input() pack: PackLite = {} as PackLite;
  @ViewChild('packTargetDomainsComponent')
    packTargetDomainsComponent?: PackTargetDomainsComponent;

  domainsList: Domain[] = [];
  marketsList: Market[] = [];
  languagesList: Language[] = [];
  statusesList: Status[] = [];
  packTypeList: PackType[] = [];
  targetDomains: TargetDomain[] = [];

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    protected dialog: MatDialog,
    private readonly configService: ConfigService,
    private readonly refDataService: RefDataService,
  ) {}

  ngOnInit(): void {
    this.refDataService
      .getByKey('domains', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allDomains: any) => {
        this.domainsList = this.refDataService.translateReference(this.refDataService.toArray(allDomains));
      });
    this.refDataService
      .getByKey('packStatuses', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allStatuses: any) => {
        this.statusesList = this.refDataService.translateReference(this.refDataService.toArray(allStatuses));
      });
    this.refDataService
      .getByKey('packTypes', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allPackTypes: any) => {
        this.packTypeList = this.refDataService.translateReference(this.refDataService.toArray(allPackTypes));
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pack?.currentValue) {
      this.pack = changes.pack.currentValue;
      this.update(this.pack);
    }
  }

  update(pack: PackLite): void {
    if (pack.key) {
      this.getMarketsByDomain(pack.domain, () => this.getLanguagesByMarket(pack.market));
      this.refDataService
        .getTargetDomainsByReferenceTypeAndKey('packs', pack.key)
        .pipe(takeUntil(this.destroy$))
        .subscribe((domainsOfPack: TargetDomain[]) => {
          this.targetDomains = domainsOfPack;
        });
    }
  }

  checkDisabledInput(): void {
    if (this.isEdit) {
      this.formPack.controls.domain.disable();
      this.formPack.controls.market.disable();
      this.formPack.controls.lang.disable();
    } else {
      this.formPack.controls.domain.value ? this.formPack.controls.market.enable() : this.formPack.controls.market.disable();

      this.formPack.controls.market.value ? this.formPack.controls.lang.enable() : this.formPack.controls.lang.disable();

      this.formPack.controls.lang.value ? this.formPack.controls.title.enable() : this.formPack.controls.title.disable();

      this.formPack.controls.lang.value
        ? this.formPack.controls.publicationStartDate.enable()
        : this.formPack.controls.publicationStartDate.disable();

      this.formPack.controls.lang.value
        ? this.formPack.controls.publicationEndDate.enable()
        : this.formPack.controls.publicationEndDate.disable();
    }
  }

  getMarketsByDomain(domainName: string, callBack: () => void): void {
    this.refDataService
      .getByKey('markets', true, domainName)
      .pipe(takeUntil(this.destroy$))
      .subscribe((allMarkets: any) => {
        this.marketsList = this.refDataService.translateReference(this.refDataService.toArray(allMarkets));
        if (callBack) {
          callBack();
        }
      });
  }

  getLanguagesByMarket(marketName: string): void {
    const market: Market = this.marketsList.find((m: Market) => marketName === m.key)!;
    let list = this.refDataService.toArray(market ? (market.langs as any) : {});
    list = list.map((data: any) => {
      const tr = this.refDataService.innerTranslate(data.name);
      return { ...data, translatedName: tr[this.configService.currentLanguage] ?? tr.en };
    });
    this.languagesList = list;
    if (this.languagesList.length === 1) {
      this.formPack.controls.lang.setValue(this.languagesList[0].key);
      this.selectLanguage(this.languagesList[0].key);
    }
  }

  selectDomain(domainName: string): void {
    this.formPack.patchValue({
      market: '',
      lang: '',
      domain: domainName,
    });
    this.getMarketsByDomain(domainName, () => {
      //Do nothing
    });
    this.checkDisabledInput();
  }

  selectMarket(marketName: string): void {
    this.formPack.patchValue({
      lang: null,
      title: '',
      shortTitle: '',
      market: marketName,
    });
    this.getLanguagesByMarket(marketName);
    this.checkDisabledInput();
  }

  selectLanguage(langName: string): void {
    this.formPack.patchValue({
      lang: langName,
    });
    this.checkDisabledInput();
  }

  toControl(resourceMedias: AbstractControl): UntypedFormControl {
    return resourceMedias as UntypedFormControl;
  }

  getName(key: string, list: DataRef[]): string {
    const obj: DataRef | undefined = (list || []).find((object: DataRef) => object.key === key);
    if (obj) {
      return obj.translatedName ?? '';
    }
    return '';
  }

  editTitles(): void {
    const data = {
      pack: this.pack,
    };
    const dialogRef: any = this.dialog.open(EditPackNamePopinComponent, { data });
    dialogRef.afterClosed().subscribe((packUpdated: PackLite) => {
      if (this.pack && this.formPack) {
        this.pack.title = packUpdated.title;
        this.formPack.patchValue({
          title: packUpdated.title,
        });
      }
    });
  }

  isOptionPublishedDisabled(status: string): boolean {
    if (status === 'PUBLISHED') {
      // if pack is published, enable the option
      if (this.pack.status === 'PUBLISHED') {
        return false;
      }
      // disable publish option for pro_coo, use publish button in recipe-tab
      if (hasTargetDomainForBinaries(this.pack)) {
        return true;
      }
    }
    return false;
  }
}
